import { useMemo } from "react";
import { AxiosError } from "axios";

import {
  QueryStatus,
  QueryEnvelope,
  EnsuredQueryEnvelope,
  ensureState,
  ensureStateWithData,
} from "../../../services/query";
import { InvitesDataEnvelope } from "../../../models/invitations";

import { useInvitesQuery } from "./invites.query";

export * from "./invites.mutations";
export * from "./invites.invalidate";

export const invitesAccessError = new Error(
  "Attempted to access invites data but it is not available",
);

// ######################
// ## useInvitesStatus ##
// ######################

export type ResultOfUseInvitesStatus = QueryStatus;
export type UseInvitesStatus = () => ResultOfUseInvitesStatus;

/**
 * The returned value of this hook will contain the status
 * of the fetching of the invites data.
 */
export const useInvitesStatus: UseInvitesStatus = () => {
  return useInvitesQuery({ watch: ["status"] }).status;
};

// #####################
// ## useInvitesError ##
// #####################

export type ResultOfUseInvitesError = AxiosError | null;
export type UseInvitesError = () => ResultOfUseInvitesError;

/**
 * The returned value of this hook will contain the error
 * that has occurred in the fetching of the invites data
 * or null if no error occurred.
 */
export const useInvitesError: UseInvitesError = () => {
  return useInvitesQuery({ watch: ["error"] }).error;
};

// #########################
// ## useMaybeInvitesData ##
// #########################

export type ResultOfUseMaybeInvitesData = InvitesDataEnvelope | undefined;
export type UseMaybeInvitesData = () => ResultOfUseMaybeInvitesData;

/**
 * The returned value of this hook will contain
 * the invites data, which may be undefined
 */
export const useMaybeInvitesData: UseMaybeInvitesData = () => {
  return useInvitesQuery({ watch: ["data"] }).data;
};

// ####################
// ## useInvitesData ##
// ####################

export type ResultOfUseInvitesData = InvitesDataEnvelope;
export type UseInvitesData = () => ResultOfUseInvitesData;

/**
 * The returned value of this hook will contain the invites data.
 *
 * This hook is guaranteed to contain invites data or will throw.
 */
export const useInvitesData: UseInvitesData = () => {
  const data = useMaybeInvitesData();
  if (data === undefined) {
    throw invitesAccessError;
  }
  return data;
};

// #####################
// ## useMaybeInvites ##
// #####################

export type ParamsOfUseMaybeInvites = { forceDisable?: boolean };
export type ResultOfUseMaybeInvites = QueryEnvelope<InvitesDataEnvelope>;
export type UseMaybeInvites = (
  params?: ParamsOfUseMaybeInvites,
) => ResultOfUseMaybeInvites;

/**
 * The `data` property returned from this hook will contain
 * the invites data, which may be undefined
 *
 * The `error` and `status` properties returned from this hook
 * will reflect the state of the invites data.
 */
export const useMaybeInvites: UseMaybeInvites = ({ forceDisable } = {}) => {
  const { status, error, data } = useInvitesQuery({
    watch: ["status", "error", "data"],
    forceDisable,
  });
  return useMemo(
    () => ensureState({ status, error, data }),
    [status, error, data],
  );
};

// ################
// ## useInvites ##
// ################

export type ResultOfUseInvites = EnsuredQueryEnvelope<InvitesDataEnvelope>;
export type UseInvites = () => ResultOfUseInvites;

/**
 * The `data` property returned from this hook will contain
 * the invites data.
 *
 * The `error` and `status` properties returned from this hook
 * will reflect the state of the invites data.
 *
 * This hook is guaranteed to contain invites data or will throw.
 */
export const useInvites: UseInvites = () => {
  const { status, error, data } = useInvitesQuery({
    watch: ["status", "error", "data"],
  });
  if (data === undefined) {
    throw invitesAccessError;
  }
  return useMemo(
    () => ensureStateWithData({ status, error, data }),
    [status, error, data],
  );
};
